import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { JwtService } from '@app/core/auth';
import { distinctUntilChanged } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageService } from '@app/services/language.service';
import { SiteSwitcherComponent } from '@app/features/sites/components/site-switcher.component';
import { UiSettingsService } from '@app/core/services/ui-settings.service';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, TranslateModule, SiteSwitcherComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <nav class="fixed top-0 left-0 right-0 z-40 navbar-dark">
      <!-- Gradient accent line at top -->
      <div class="navbar-accent-line"></div>
      <div class="max-w-7xl mx-auto px-4 sm:px-8">
        <div class="flex items-center justify-between h-15">
          <!-- Left: Logo + tabs -->
          <div class="flex items-center gap-8">
            <div class="flex items-center gap-3">
              <div class="navbar-logo-container">
                <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                </svg>
              </div>
              <div>
                <h1 class="text-sm font-bold tracking-tight" style="color: #ffffff; letter-spacing: -0.02em;">{{ 'SIDEBAR.TITLE' | translate }}</h1>
              </div>
            </div>

            <nav class="flex items-center gap-1">
              <a
                routerLink="/bookings"
                routerLinkActive="active-tab"
                [routerLinkActiveOptions]="{ exact: true }"
                class="tab-link"
              >
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                </svg>
                <span class="hidden sm:inline">{{ 'SIDEBAR.RESERVATIONS' | translate }}</span>
              </a>
              @if ((isAdmin$ | async) && (enabledModules$ | async)?.tables) {
                <a
                  routerLink="/admin/tables"
                  routerLinkActive="active-tab"
                  class="tab-link"
                  data-testid="nav-admin-tables"
                >
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z"></path>
                  </svg>
                  <span class="hidden sm:inline">{{ 'SIDEBAR.FLOOR_PLAN' | translate }}</span>
                </a>
              }
              @if ((isAdmin$ | async) && (enabledModules$ | async)?.vehicles) {
                <a
                  routerLink="/admin/vehicles"
                  routerLinkActive="active-tab"
                  class="tab-link"
                  data-testid="nav-admin-vehicles"
                >
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0zM13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"></path>
                  </svg>
                  <span class="hidden sm:inline">{{ 'SIDEBAR.ADMIN_VEHICLES' | translate }}</span>
                </a>
              }
              @if ((isAdmin$ | async) && (enabledModules$ | async)?.rooms) {
                <a
                  routerLink="/admin/rooms"
                  routerLinkActive="active-tab"
                  class="tab-link"
                  data-testid="nav-admin-rooms"
                >
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                  </svg>
                  <span class="hidden sm:inline">{{ 'SIDEBAR.ADMIN_ROOMS' | translate }}</span>
                </a>
              }
              <a
                routerLink="/settings"
                routerLinkActive="active-tab"
                class="tab-link"
                data-testid="nav-settings"
              >
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                <span class="hidden sm:inline">{{ 'SIDEBAR.SETTINGS' | translate }}</span>
              </a>
            </nav>
          </div>

          <!-- Right: Site switcher + Lang + User -->
          <div class="flex items-center gap-3">
            <app-site-switcher></app-site-switcher>

            <div class="relative">
              <select (change)="switchLang($any($event.target).value)" [value]="currentLang" class="navbar-lang-select">
                <option value="fr">FR</option>
                <option value="en">EN</option>
              </select>
            </div>

            @if (user.name) {
              <div class="flex items-center gap-2.5 pl-3" style="border-left: 1px solid rgba(255,255,255,0.1);">
                <div class="navbar-avatar">
                  <span class="text-white font-bold text-xs">{{ getInitials(user.name) }}</span>
                </div>
                <div class="hidden md:block">
                  <p class="text-sm font-medium leading-tight" style="color: #e2e8f0;">{{ user.name }}</p>
                  <p class="text-xs leading-tight" style="color: #64748b;">{{ user.organisation }}</p>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </nav>
  `,
  styles: [`
    .navbar-dark {
      background: linear-gradient(180deg, #0a1020 0%, #111a33 100%);
      border-bottom: 1px solid rgba(99, 102, 241, 0.08);
      box-shadow: 0 8px 32px rgba(10, 16, 32, 0.4), 0 1px 3px rgba(10, 16, 32, 0.2);
    }
    .navbar-accent-line {
      height: 2px;
      background: linear-gradient(90deg, #6366f1, #8b5cf6, #a855f7, #ec4899, #8b5cf6, #6366f1);
      background-size: 300% 100%;
      animation: gradient-shift 8s ease-in-out infinite;
    }
    @keyframes gradient-shift {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    .navbar-logo-container {
      width: 34px;
      height: 34px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #6366f1, #8b5cf6);
      box-shadow: 0 2px 16px rgba(99, 102, 241, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.2);
    }
    .h-15 {
      height: 3.75rem;
    }
    .tab-link {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.4rem 0.875rem;
      font-size: 0.8125rem;
      font-weight: 500;
      color: #94a3b8;
      border-radius: 0.625rem;
      transition: all 0.2s ease;
    }
    .tab-link:hover {
      color: #e2e8f0;
      background: rgba(255, 255, 255, 0.06);
    }
    .tab-link:focus-visible {
      outline: 2px solid rgba(99, 102, 241, 0.6);
      outline-offset: 2px;
    }
    .active-tab {
      color: #ffffff !important;
      background: rgba(99, 102, 241, 0.18) !important;
      box-shadow: inset 0 0 0 1px rgba(99, 102, 241, 0.25), 0 0 12px rgba(99, 102, 241, 0.08);
    }
    .navbar-lang-select {
      appearance: none;
      font-size: 0.75rem;
      font-weight: 600;
      padding: 0.375rem 0.75rem;
      border-radius: 0.5rem;
      cursor: pointer;
      transition: all 0.15s ease;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.08);
      color: #94a3b8;
    }
    .navbar-lang-select:hover {
      background: rgba(255, 255, 255, 0.1);
      color: #e2e8f0;
    }
    .navbar-lang-select:focus-visible {
      outline: 2px solid rgba(99, 102, 241, 0.6);
      outline-offset: 2px;
    }
    .navbar-lang-select option {
      background: #1e293b;
      color: #e2e8f0;
    }
    .navbar-avatar {
      width: 36px;
      height: 36px;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      background: linear-gradient(135deg, #6366f1, #a855f7);
      box-shadow: 0 2px 12px rgba(99, 102, 241, 0.4);
      border: 2px solid rgba(255, 255, 255, 0.12);
    }
  `]
})
export class NavbarComponent implements OnInit {
  private jwtService = inject(JwtService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private languageService = inject(LanguageService);
  private uiSettingsService = inject(UiSettingsService);

  readonly isAdmin$ = this.jwtService.isAdmin$;
  readonly enabledModules$ = this.uiSettingsService.enabledModules$;

  user = { name: '', organisation: '' };
  currentLang = 'fr';

  ngOnInit() {
    this.jwtService.token$.pipe(
      distinctUntilChanged((prev, curr) => prev?.sub === curr?.sub),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(decodedToken => {
      if (decodedToken?.name && decodedToken?.sub) {
        this.user.name = decodedToken.name;
        this.user.organisation = decodedToken.organization ?? '';
      } else {
        this.user.name = '';
        this.user.organisation = '';
      }
      this.cdr.markForCheck();
    });

    this.languageService.currentLanguage$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(lang => {
      this.currentLang = lang;
      this.cdr.markForCheck();
    });

    setTimeout(() => this.jwtService.updateTokenObservable(), 100);
  }

  getInitials(name: string): string {
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  }

  switchLang(lang: string) {
    this.languageService.setLanguage(lang);
  }
}
